import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, AlertCircle, Users } from "lucide-react";
import { useDrivers } from "@/hooks/useDrivers";
import DriverCard from "@/components/drivers/DriverCard";
import DriverModal from "@/components/drivers/DriverModal";

export default function DriversPage() {
  const { t } = useTranslation("pages");
  const { data, isLoading, error } = useDrivers();
  const drivers = data?.drivers;
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);

  const handleDriverClick = (driverId: string) => {
    setSelectedDriverId(driverId);
  };

  const handleCloseModal = () => {
    setSelectedDriverId(null);
  };

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="text-secondary-600">{t("drivers.loading")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">
            {t("drivers.errorTitle")}
          </h2>
          <p className="text-secondary-600">{t("drivers.errorMessage")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="mb-3 sm:mb-8">
        <h1 className="page-title">{t("drivers.title")}</h1>
        <p className="page-subtitle">
          {t("drivers.pageSubtitle")}
        </p>
      </div>

      {drivers && drivers.length > 0 ? (
        <>
          <div className="mb-4 sm:mb-6">
            <p className="text-sm text-secondary-600">
              {t("drivers.driverCounts", {
                publicCount: data?.publicCount ?? 0,
                totalCount: data?.totalCount ?? 0,
              })}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {drivers.map((driver) => (
              <DriverCard
                key={driver.id}
                driver={driver}
                onClick={() => handleDriverClick(driver.id)}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-8 sm:py-12">
          <Users className="h-10 w-10 sm:h-12 sm:w-12 text-secondary-400 mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-semibold text-secondary-900 mb-2">
            {t("drivers.noDrivers")}
          </h2>
          <p className="text-secondary-600 text-sm sm:text-base px-4">
            {t("drivers.noDriversMessage")}
          </p>
        </div>
      )}

        {selectedDriverId && (
          <DriverModal
            isOpen={true}
            onClose={handleCloseModal}
            driverId={selectedDriverId}
          />
        )}
    </div>
  );
}
